import React from "react";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tonal-shift surface — `cream-100` patch on a `cream-50` page background. */
  tone?: "raised" | "flat" | "ink";
  /** Adds an interactive 1px coffee border. Use for clickable cards. */
  interactive?: boolean;
  padded?: boolean;
  /** Backwards-compat. Has no visual effect — the system has no shadows. */
  elevated?: boolean;
  /** Backwards-compat. Switches to interactive border. */
  bordered?: boolean;
}

const TONE: Record<NonNullable<CardProps["tone"]>, string> = {
  raised: "bg-cream-100 text-coffee-900 dark:bg-coffee-800 dark:text-cream-50",
  flat:   "bg-cream-50 text-coffee-900 dark:bg-coffee-900 dark:text-cream-50",
  ink:    "bg-coffee-900 text-cream-50 dark:bg-cream-100 dark:text-coffee-900",
};

/**
 * Card — tonal-shift surface, no shadow, no border by default.
 * The "raise" comes from a slightly warmer or cooler patch of cream against the
 * cream-50 page background. Borders only when interactive.
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    tone = "raised",
    interactive = false,
    padded = true,
    elevated: _elevated,    // eslint-disable-line @typescript-eslint/no-unused-vars
    bordered,
    className = "",
    children,
    ...rest
  },
  ref
) {
  const cls = [
    "rounded-lg",
    TONE[tone],
    interactive || bordered
      ? "border border-latte-300 dark:border-latte-800 hover:border-coffee-900 dark:hover:border-cream-50 transition-colors"
      : "",
    padded ? "p-6 sm:p-8" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={ref} className={cls} {...rest}>
      {children}
    </div>
  );
});

export default Card;
